import moment from "moment";
import { TitleAPI } from "../../../../API/title";
import { getTitleCod } from "../../../utils/utils";

/**
 * Calcula a variação do IPCA entre duas datas.
 */

function calculateIPCARateChange(ipcaData, startDate, endDate) {

  const parseDMY = (s) => {
    const [d, m, y] = String(s).split('/').map(Number);
    return { d, m, y };
  };

  const toTs = (s) => {
    const { d, m, y } = parseDMY(s);
    return new Date(y, m - 1, d).getTime();
  };

  const startTs = toTs(startDate);
  const endTs = toTs(endDate);

  // Espera ipcaData.data = { "M/YYYY": [ { date, ipca_dp }, ... ] }
  if (!ipcaData || !ipcaData.data) return 0;

  // Gera as chaves "M/YYYY" entre start e end (inclusive)
  const listMonthKeysBetween = (s, e) => {
    const { m: sm, y: sy } = parseDMY(s);
    const { m: em, y: ey } = parseDMY(e);
    const keys = [];
    let y = sy, m = sm;
    while (y < ey || (y === ey && m <= em)) {
      keys.push(`${m}/${y}`);
      m++;
      if (m > 12) { m = 1; y++; }
    }
    return keys;
  };

  // Coleta os pontos relevantes apenas dos meses do período
  const monthKeys = listMonthKeysBetween(startDate, endDate);
  let candidates = [];
  for (const key of monthKeys) {
    const arr = ipcaData.data[key];
    if (Array.isArray(arr) && arr.length) {
      candidates.push(...arr);
    }
  }

  if (candidates.length === 0) return 0;

  // Filtra por data exata (caso pegue meses de borda com dias fora)
  candidates = candidates.filter(item => {
    const ts = toTs(item.date);
    return ts >= startTs && ts <= endTs;
  });

  if (candidates.length === 0) return 0;

  // Acha os pontos mais próximos de start e end
  const findClosest = (targetTs) => {
    return candidates.reduce((closest, current) => {
      const curTs = toTs(current.date);
      const cloTs = toTs(closest.date);
      return Math.abs(curTs - targetTs) < Math.abs(cloTs - targetTs) ? current : closest;
    });
  };

  const startItem = findClosest(startTs);
  const endItem = findClosest(endTs);

  if (!startItem || !endItem) return 0;

  const startValue = startItem.ipca_dp;
  const endValue = endItem.ipca_dp;
  if (!startValue || !endValue || startValue === 0) return 0;

  // Variação percentual entre os dois valores acumulados
  const ipcaChange = (endValue / startValue) - 1;
  return ipcaChange;
}

/**
 * Converte uma taxa anual para a taxa equivalente em um período de meses.
 */
function convertAnnualRateToPeriod(annualRate, months) {
  return Math.pow(1 + annualRate, months / 12) - 1;
}

/**
 * Calcula a quantidade de meses entre duas datas.
 */
function calculateMonthsBetweenDates(startDate, endDate) {
  const start = moment(startDate, 'DD/MM/YYYY');
  const end = moment(endDate, 'DD/MM/YYYY');
  return Math.max(1, end.diff(start, 'months', true));
}

/**
 * Encontra o PU mais próximo de uma data alvo.
 */
export function findClosestPU(quotasObj, date) {
  if (!quotasObj || !quotasObj.data) return null;

  const target = moment(date, "DD/MM/YYYY", true);
  if (!target.isValid()) return null;

  const month = target.month() + 1; // 0-based -> 1..12
  const year = target.year();
  const key = `${month}/${year}`;

  // 1) Tenta primeiro no mês/ano da data alvo
  let candidates = quotasObj.data[key] || [];

  // 2) Se não houver nesse mês, tenta mês anterior e seguinte (limites do período)
  if (candidates.length === 0) {
    const prevKey = `${target.clone().subtract(1, "month").month() + 1}/${target.clone().subtract(1, "month").year()}`;
    const nextKey = `${target.clone().add(1, "month").month() + 1}/${target.clone().add(1, "month").year()}`;

    candidates = [
      ...(quotasObj.data[prevKey] || []),
      ...(quotasObj.data[nextKey] || [])
    ];
  }

  // 3) Se ainda vazio (caso extremo), considera todos os meses disponíveis no objeto
  if (candidates.length === 0) {
    // Observação: isso NÃO altera a estrutura original, apenas cria uma lista temporária local
    candidates = Object.values(quotasObj.data).flat();
  }

  if (candidates.length === 0) return null;

  // 4) Encontra o item com date_quota mais próximo de target
  let closest = null;
  let minDiff = Infinity;

  for (const item of candidates) {
    const d = moment(item?.date_quota, "DD/MM/YYYY", true);
    if (!d.isValid() || typeof item?.pu_anb !== "number") continue;

    const diff = Math.abs(d.diff(target, "days"));
    if (diff < minDiff) {
      minDiff = diff;
      closest = item;
    }
  }

  return closest ? closest.pu_anb : null;
}

/**
 * Calcula a performance do título segundo a curva (PU + IPCA + taxa pré-fixada)
 */
function calculateCurvePerformance(title, startDate, endDate, ipcaDp, quotasAnb) {
  try {
    let initialPU = findClosestPU(quotasAnb, startDate);
    if (!initialPU) {
      console.error('PU inicial não encontrado para a curva:', title.title_name);
      return null;
    }

    let preRate = parseFloat(title.pre_tax) / 100;
    if (isNaN(preRate)) preRate = 0;

    const months = calculateMonthsBetweenDates(startDate, endDate);

    const ipcaChange = calculateIPCARateChange(ipcaDp, startDate, endDate);
    const preRatePeriod = convertAnnualRateToPeriod(preRate, months);
    const totalRate = (1 + preRatePeriod) * (1 + ipcaChange) - 1;

    const finalPU = initialPU * (1 + totalRate);
    const quantity = parseFloat(title.quota_amount_now || title.quantity_purchased);

    const curveAmount = finalPU * quantity;
    const curveYield = (finalPU - initialPU) / initialPU;
    const curveReturn = (finalPU - initialPU) * quantity;

    return { curveAmount, curveYield, curveReturn };
  } catch (error) {
    console.error('Erro ao calcular performance da curva:', error, title.title_name);
    return null;
  }
}

/**
 * Calcula a performance do título segundo o mercado (PU + cupons)
 */
async function calculateMarketPerformance(title, startDate, endDate, quotasAnb) {
  try {
    const startYear = moment(startDate, "DD/MM/YYYY").year();
    const endYear = moment(endDate, "DD/MM/YYYY").year();
    const years = [];

    for (let year = startYear; year <= endYear; year++) {
      years.push(year);
    }

    const couponsResponses = await Promise.all(
      years.map((year) => TitleAPI.getCouponsByTitleIdAndYear(title.title_id, year))
    );

    const coupons = couponsResponses.flatMap((response) => response?.body?.rows || []);

    const start = moment(startDate, "DD/MM/YYYY").startOf("day");
    const end = moment(endDate, "DD/MM/YYYY").endOf("day");

    // Filtra cupons dentro do período
    const filteredCoupons = coupons.filter(c => {
      const couponDate = moment.utc(c.date_coupon);
      return couponDate.isBetween(start, end, undefined, "[]");
    });

    let initialPU = findClosestPU(quotasAnb, startDate);
    let marketPU = findClosestPU(quotasAnb, endDate);

    if (!initialPU || !marketPU) {
      console.error('PU não encontrado para cálculo de mercado:', title.title_name);
      return null;
    }

    const totalCoupons = filteredCoupons.reduce((acc, c) => acc + parseFloat(c.value_coupon || 0), 0);
    const quantity = parseFloat(title.quantity_purchased || title.quota_amount_now || 0);

    const marketAmount = marketPU * quantity;
    const marketYield = (marketPU + totalCoupons - initialPU) / initialPU;
    const marketReturn = (marketPU + totalCoupons - initialPU) * quantity;

    return { marketAmount, marketYield, marketReturn };
  } catch (error) {
    console.error('Erro ao calcular performance de mercado:', error, title.title_name);
    return null;
  }
}

/**
 * Calcula a performance completa do título (curva + mercado)
 */
export async function calculateTitlePerformance(title, startDate, endDate, ipcaDpInPeriod, quotasAnbList, periodType) {
  try {
    const match = quotasAnbList?.find(
      (x) => Number(x.titleId) === Number(title.title_id)
    );

    const quotasAnbInPeriod = match?.quotasAnb || null;

    let effectiveStartDate = startDate;
    let effectiveEndDate = endDate;

    if (periodType === 'fromStart') {
      const purchaseDate = moment.utc(title.purchase_date).format('DD/MM/YYYY');
      if (moment(purchaseDate, 'DD/MM/YYYY', true).isValid()) {
        effectiveStartDate = purchaseDate;
      }

      if (moment(effectiveStartDate, 'DD/MM/YYYY', true).isAfter(moment(effectiveEndDate, 'DD/MM/YYYY', true))) {
        effectiveEndDate = effectiveStartDate;
      }
    }

    const balanceNow = title.balance_now !== null && title.balance_now !== undefined
      ? parseFloat(title.balance_now)
      : null;
    const rentMoneyPeriod = title.rentMoneyPeriod !== null && title.rentMoneyPeriod !== undefined
      ? parseFloat(title.rentMoneyPeriod)
      : null;
    const rentPeriod = title.rentPeriod !== null && title.rentPeriod !== undefined
      ? parseFloat(title.rentPeriod)
      : null;

    const curveResultFromBalance = (balanceNow !== null && rentMoneyPeriod !== null && rentPeriod !== null) ? {
      curveAmount: balanceNow,
      curveYield: rentPeriod / 100,
      curveReturn: rentMoneyPeriod
    } : null;

    const curveResult = curveResultFromBalance || calculateCurvePerformance(
      title,
      effectiveStartDate,
      effectiveEndDate,
      ipcaDpInPeriod,
      quotasAnbInPeriod
    );
    const marketResult = await calculateMarketPerformance(title, effectiveStartDate, effectiveEndDate, quotasAnbInPeriod);

    const isMarkedAtMarket = title.marking && title.marking.toUpperCase() === 'MERCADO';

    let difference = 0;
    if (curveResult && marketResult) {
      difference = isMarkedAtMarket
        ? marketResult.marketAmount - curveResult.curveAmount
        : curveResult.curveAmount - marketResult.marketAmount;
    } else if (marketResult && !curveResult) {
      difference = isMarkedAtMarket ? marketResult.marketAmount : -marketResult.marketAmount;
    } else if (curveResult && !marketResult) {
      difference = isMarkedAtMarket ? -curveResult.curveAmount : curveResult.curveAmount;
    }

    const curveYieldValue = curveResult?.curveYield;
    const marketYieldValue = marketResult?.marketYield;

    return {
      marking: title.marking,
      purchaseDate: moment.utc(title.purchase_date).format('DD/MM/YYYY'),
      titleName: title.title_name,

      // Dados da curva
      curveAmount: curveResult?.curveAmount ?? null,
      curveYield: curveYieldValue === null || curveYieldValue === undefined ? null : (curveYieldValue * 100).toFixed(2),
      curveReturn: curveResult?.curveReturn ?? null,

      // Dados do mercado
      marketAmount: marketResult?.marketAmount ?? null,
      marketYield: marketYieldValue === null || marketYieldValue === undefined ? null : (marketYieldValue * 100).toFixed(2),
      marketReturn: marketResult?.marketReturn ?? null,

      // Diferença
      difference,

      // Flags
      hasCurveCalculation: !!curveResult,
      hasMarketCalculation: !!marketResult
    };
  } catch (error) {
    console.error('Erro ao calcular performance do título:', error);
    return null;
  }
}

/**
 * Calcula os totais de um conjunto de análises de títulos
 */
export function calculateTotals(titleAnalytics) {
  if (!titleAnalytics || titleAnalytics.length === 0) {
    return {
      totalCurveAmount: 0,
      totalMarketAmount: 0,
      totalDifference: 0,
      titleCount: 0
    };
  }

  let totalCurveAmount = 0;
  let totalMarketAmount = 0;
  let totalCount = 0;

  let totalDifference = 0;

  for (const analytics of titleAnalytics) {
    if (!analytics) continue;

    if (analytics.hasCurveCalculation && analytics.curveAmount !== null && analytics.curveAmount !== undefined)
      totalCurveAmount += analytics.curveAmount;

    if (analytics.hasMarketCalculation && analytics.marketAmount !== null && analytics.marketAmount !== undefined)
      totalMarketAmount += analytics.marketAmount;

    if (analytics.difference !== null && analytics.difference !== undefined)
      totalDifference += analytics.difference;

    if (analytics.hasCurveCalculation || analytics.hasMarketCalculation)
      totalCount++;
  }

  return {
    totalCurveAmount,
    totalMarketAmount,
    totalDifference,
    titleCount: totalCount
  };
}
